<script setup lang="ts">
    import { ref, toRefs, computed } from "vue";
    import emitter from "tiny-emitter/instance";

    import ModalBasic from "@/components/generic/ModalBasic.vue";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import { openDialog } from "@/utils";
    import store from "@/store";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { AxiosError } from "axios";
    import { onBeforeMount, onBeforeUnmount } from "vue";
    import { flashWarning } from "@/utils";
    import { NumberValidationRule } from "@/components/forms/inputs/Number/numberValidation";
    import { mapTypAvGenomlysningsapparat } from "@/store/modules/typAvGenomlysningsapparatModule";

    // TODO Ändra i Vue3
    interface Genomlysningsapparat {
        id: number;
        namn: string;
        typ: number;
        ordning: number;
    }

    const props = defineProps<{
        tabId: number; // TODO Ändra i Vue3
        parameters?: {
            genomlysningsapparatId: string;
        };
    }>();

    const { parameters, tabId } = toRefs(props);

    const errors = ref<{
        [key: string]: string[];
    }>({});
    const dialog = ref();
    const formRef = ref();

    const genomlysningsapparat = computed(() =>
        store.getters["genomlysningsapparat/genomlysningsapparatById"]({
            id: parameters?.value?.genomlysningsapparatId,
        })
    );
    const isEditing = computed(() => !!genomlysningsapparat.value);

    defineExpose({
        tabId,
    });

    const cancelForm = async () => {
        if (!formRef.value.hasUnsavedData()) {
            store.dispatch("tabs/closeTab", tabId.value);

            return;
        }

        emitter.emit("open-dialog", {
            type: "warning",
            description: "Du har osparad data. Vill du stänga utan att spara?",
            buttons: [
                {
                    title: "Fortsätt redigera",
                    type: "secondary",
                },
                {
                    title: "Ja, stäng utan att spara",
                    type: "primary",
                    action: () => {
                        store.dispatch("tabs/closeTab", tabId.value);
                    },
                },
            ],
        });
    };

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: isEditing.value ? "Redigera genomlysningsapparat" : "Skapa genomlysningsapparat",
        });
    };

    // TODO convert to useTabs in v3
    const closeTab = ({
        override,
    }: {
        override?: boolean;
    } = {}) => {
        if (!override && formRef.value.hasUnsavedData()) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const onSubmit = async (genomlysningsapparat: unknown) => {
        try {
            if (isEditing.value) {
                await store.dispatch("genomlysningsapparat/updateGenomlysningsapparat", {
                    genomlysningsapparat: genomlysningsapparat as Genomlysningsapparat,
                });
            } else {
                await store.dispatch("genomlysningsapparat/createGenomlysningsapparat", {
                    genomlysningsapparat: genomlysningsapparat as Genomlysningsapparat,
                });
            }

            closeTab({ override: true });
        } catch (thrownError) {
            const error = thrownError as AxiosError;

            if (error?.response?.data) {
                errors.value = error.response.data;

                if (error?.response?.data?.non_field_errors && error?.response?.data?.non_field_errors.length > 0) {
                    openDialog(error?.response?.data?.non_field_errors[0], "error");
                }

                return;
            }

            flashWarning(
                isEditing.value
                    ? "Kunde inte redigera genomlysningsapparat"
                    : "Kunde inte lägga till ny genomlysningsapparat"
            );
        }
    };

    const fetchTyperAvGenomlysningsapparater = () => {
        const typer = store.getters["typAvGenomlysningsapparat/typerAvGenomlysningsapparater"];

        return typer.map(mapTypAvGenomlysningsapparat);
    };

    const fetchFormData = async () => {
        return (
            genomlysningsapparat.value ?? {
                namn: null,
                typ: null,
                ordning: null,
            }
        );
    };

    onBeforeMount(async () => {
        await store.dispatch("genomlysningsapparat/addSubscriber");

        setTabName();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("genomlysningsapparat/removeSubscriber");
    });
</script>

<template>
    <div class="genomlysningsapparat">
        <modal-basic ref="dialog" />
        <KForm
            ref="formRef"
            id="genomlysningsapparat"
            :use-formulate="true"
            :submit="onSubmit"
            submit-content="Spara och stäng"
            cancel-content="Avbryt"
            :errors="errors"
            :initial-values-getter="fetchFormData"
            @cancel="cancelForm"
            class="genomlysningsapparat-form"
        >
            <template #header>
                <admin-lock-border />
                <h2 v-if="isEditing">Redigera genomlysningsapparat</h2>
                <h2 v-else>Lägg till genomlysningsapparat</h2>
            </template>
            <template #default>
                <div class="form-content">
                    <KText name="namn" label="Namn" validation="required" />

                    <KAutocomplete name="typ" label="Typ" :options="fetchTyperAvGenomlysningsapparater" />

                    <KNumber name="ordning" label="Ordning" :validation="[[NumberValidationRule.Min, 0]]" />
                </div>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss"></style>
